import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';
import { mulberry32 } from '../core/rng.js';
import { stampHouse } from './props.js';
import { PICK_MATERIAL } from '../core/materials.js';
import { LANDMARKS } from '../config.js';

// ---------------------------------------------------------------------------
// Marken, and the Paard van Marken.
//
// The real one is a square white tower on a brick base, standing at the far
// end of the island with the water on three sides — the Horse of Marken. It is
// the only thing out here tall enough to see from the other islands, so it is
// the island's whole silhouette, and after dark its beam is the only thing
// moving on the water.
// ---------------------------------------------------------------------------

export function registerMarkenBlocks(palette) {
    return {
        stone: palette.add('markenStone', 0x6d6357, { jitter: 0.07 }),
        tower: palette.add('markenTower', 0xe8e4d6, { jitter: 0.035 }),
        band: palette.add('markenBand', 0x2b2f36, { jitter: 0.05 }),
        gallery: palette.add('markenGallery', 0x3c424a, { jitter: 0.05 }),
        lamp: palette.add('markenLamp', 0xffe9a8, { emissive: 1.0, jitter: 0.02 }),
        wall: palette.add('markenWall', 0x24402f, { jitter: 0.05 }),   // tarred green boarding
        roof: palette.add('markenRoof', 0x4a4f57, { jitter: 0.06 }),
    };
}

const SEA_Y = 2.4;  // the waterline in world units
const BASE = 9;     // brick base, a square
const TOWER = 5;    // the tower itself
const HEIGHT = 20;  // voxels from the base to the gallery

function buildTower(palette, material, P, { voxelSize }) {
    const g = new VoxelGrid(BASE, HEIGHT + 8, BASE);
    const c = (BASE - 1) / 2;
    const t0 = Math.round(c - (TOWER - 1) / 2);
    const t1 = t0 + TOWER - 1;

    // The base: a stone block with a battered edge, standing in the water.
    g.box(0, 0, 0, BASE - 1, 3, BASE - 1, P.stone);
    g.box(1, 4, 1, BASE - 2, 5, BASE - 2, P.stone);

    // The tower: square, white, with the dark band it actually wears.
    g.box(t0, 6, t0, t1, 6 + HEIGHT, t1, P.tower);
    g.box(t0, 6 + Math.round(HEIGHT * 0.55), t0, t1, 7 + Math.round(HEIGHT * 0.55), t1, P.band);

    // Windows up the seaward face.
    for (let y = 9; y < 6 + HEIGHT - 2; y += 4) {
        g.set(t0, y, c, P.band);
        g.set(t1, y, c, P.band);
    }

    // The gallery and the lantern room on top of it.
    const gy = 7 + HEIGHT;
    g.box(t0 - 1, gy, t0 - 1, t1 + 1, gy, t1 + 1, P.gallery);
    g.box(t0, gy + 1, t0, t1, gy + 3, t1, P.lamp);
    g.box(t0 - 1, gy + 4, t0 - 1, t1 + 1, gy + 4, t1 + 1, P.gallery);
    g.set(c, gy + 5, c, P.band);

    const mesh = new THREE.Mesh(
        g.build(palette, {
            voxelSize,
            origin: new THREE.Vector3(-c * voxelSize, 0, -c * voxelSize),
        }),
        material,
    );
    mesh.name = 'paard-van-marken';
    return { mesh, lampY: (gy + 2) * voxelSize };
}

// A flat additive cone reads as a cardboard wedge: hard edges, uniform
// brightness, obviously a piece of geometry. The fix is John Chapman's "good
// enough" volumetrics, the same trick threex.volumetricspotlight uses — fade
// the cone by how edge-on you are seeing it, and by distance from the lamp.
//
// Seen face-on you are looking through the thin part of the beam and it should
// nearly vanish; seen edge-on you are looking down its length through far more
// lit air, and it should be bright. That one term is most of the effect.
const BEAM_VERT = /* glsl */ `
varying vec3 vNormalW;
varying vec3 vWorldPos;

void main() {
    vNormalW = normalize(normalMatrix * normal);
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorldPos = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const BEAM_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform vec3 uLamp;
uniform float uReach;
uniform float uEdge;
uniform float uStrength;
uniform vec3 uFogColor;
uniform vec2 uFogRange;

varying vec3 vNormalW;
varying vec3 vWorldPos;

void main() {
    // Edge-on is bright, face-on is nearly nothing.
    vec3 view = normalize(cameraPosition - vWorldPos);
    float rim = pow(abs(dot(normalize(vNormalW), view)), uEdge);

    // And it runs out with distance, like a real beam in damp air.
    float reach = 1.0 - clamp(distance(vWorldPos, uLamp) / uReach, 0.0, 1.0);
    reach *= reach;

    // Never a hard tip: the last of it dissolves rather than stopping.
    float alpha = rim * reach * uStrength;
    if (alpha < 0.002) discard;

    // The beam is in the same weather as everything else.
    float fog = smoothstep(uFogRange.x, uFogRange.y, length(cameraPosition - vWorldPos));
    vec3 col = mix(uColor, uFogColor, fog * 0.5);
    gl_FragColor = vec4(col * alpha, alpha);
}
`;

/**
 * The beam: a cone lying on its side, turned slowly, and only after dark —
 * by day a lighthouse is a tower, not a searchlight.
 */
function buildBeam(env, voxelSize, lampY) {
    const LENGTH = 62;
    // Open-ended: a capped cone shows its base as a bright disc when the beam
    // sweeps towards you.
    const geometry = new THREE.ConeGeometry(4.2, LENGTH, 24, 12, true);
    geometry.rotateZ(Math.PI / 2);          // point it along +X
    geometry.translate(LENGTH / 2, 0, 0);   // pivot at the lamp, not the middle

    const material = new THREE.ShaderMaterial({
        vertexShader: BEAM_VERT,
        fragmentShader: BEAM_FRAG,
        uniforms: {
            uColor: { value: new THREE.Color(0xffe6a4) },
            uLamp: { value: new THREE.Vector3() },
            uReach: { value: LENGTH },
            uEdge: { value: 2.6 },
            uStrength: { value: 0 },
            uFogColor: env.uniforms.uFogColor,
            uFogRange: env.uniforms.uFogRange,
        },
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
    });

    const beam = new THREE.Mesh(geometry, material);
    beam.renderOrder = 4;
    beam.frustumCulled = false;

    // Tilted down so the beam lands on the water rather than running off level
    // with the horizon. The run is the beam's whole length: shortening it here
    // steepens the angle and drives the tip under the surface, where the last
    // stretch of it draws over the sea instead of grazing it.
    beam.rotation.z = -Math.atan2(lampY - SEA_Y - 1.0, LENGTH);

    // The tilt lives on the beam and the sweep on the pivot above it. Both on
    // one object and the Euler order swings the whole cone round a cocked
    // axis, so it rises and dips as it turns.
    const pivot = new THREE.Group();
    pivot.position.y = lampY;
    pivot.add(beam);
    pivot.beam = beam;
    return pivot;
}

export function buildMarken(scene, grid, polder, palette, material, P, props, env, { voxelSize, seed, facing }) {
    const rng = mulberry32(seed);
    const { size, seaLevel } = polder;
    const mid = size / 2;

    // Put it at the far end from Volendam, where the water is on three sides.
    const bearing = Math.atan2(1, 0.55);
    let coast = 0;
    for (let r = 0; r < size / 2; r += 0.5) {
        const x = Math.round(mid + Math.sin(bearing) * r);
        const z = Math.round(mid + Math.cos(bearing) * r);
        if (polder.top(x, z) >= seaLevel) coast = r;
    }

    // A stone causeway out to it, so it stands off the beach rather than on it.
    const stand = coast + 2.5;
    for (let r = coast - 2; r <= stand + 1; r += 0.5) {
        const x = Math.round(mid + Math.sin(bearing) * r);
        const z = Math.round(mid + Math.cos(bearing) * r);
        for (let dx = -1; dx <= 1; dx++) {
            for (let dz = -1; dz <= 1; dz++) {
                for (let y = seaLevel - 2; y <= seaLevel; y++) grid.set(x + dx, y, z + dz, P.stone);
                const i = (z + dz) * size + (x + dx);
                if (i >= 0 && i < polder.heights.length) {
                    polder.heights[i] = Math.max(polder.heights[i], seaLevel + 1);
                    polder.surface[i] = P.stone;
                }
            }
        }
    }

    const gx = mid + Math.sin(bearing) * stand;
    const gz = mid + Math.cos(bearing) * stand;
    const at = polder.toWorld(gx, gz);

    const { mesh, lampY } = buildTower(palette, material, P, { voxelSize });
    const group = new THREE.Group();
    group.position.set(at.x, (seaLevel - 1) * voxelSize, at.z);
    group.add(mesh);

    const pivot = buildBeam(env, voxelSize, lampY);
    const beam = pivot.beam;
    group.add(pivot);
    scene.add(group);

    // Something to point at. The tower is a thin thing at any distance, so the
    // target is a generous column around it rather than the geometry itself.
    const pick = new THREE.Mesh(
        new THREE.CylinderGeometry(BASE * 0.75 * voxelSize, BASE * 0.8 * voxelSize, (HEIGHT + 12) * voxelSize, 10),
        PICK_MATERIAL,
    );
    pick.position.y = ((HEIGHT + 12) / 2) * voxelSize;
    pick.userData.landmark = LANDMARKS.marken;
    group.add(pick);

    // A standing glow at the foot, so the tower is lit rather than a silhouette.
    env.addLight(new THREE.Vector3(at.x, (seaLevel + 3) * voxelSize, at.z), {
        color: 0xffe9a8, intensity: 1.4, range: 20 * voxelSize * 2.6,
    });

    // --- the ferry quay ----------------------------------------------------
    // On the side that looks at Volendam, because that is the crossing. A
    // stone landing and a short stub of pier: enough to lie against, not
    // enough to be a harbour.
    let quayCoast = 0;
    for (let r = 0; r < size / 2; r += 0.5) {
        const x = Math.round(mid + Math.sin(facing) * r);
        const z = Math.round(mid + Math.cos(facing) * r);
        if (polder.top(x, z) >= seaLevel) quayCoast = r;
    }

    const quayEnd = quayCoast + 4;
    for (let r = quayCoast - 3; r <= quayEnd; r += 0.5) {
        const x = Math.round(mid + Math.sin(facing) * r);
        const z = Math.round(mid + Math.cos(facing) * r);
        for (let dx = -2; dx <= 2; dx++) {
            for (let dz = -2; dz <= 2; dz++) {
                if (Math.hypot(dx, dz) > 2.4) continue;
                for (let y = seaLevel - 2; y <= seaLevel + 1; y++) {
                    grid.set(x + dx, y, z + dz, P.stone);
                }
                const i = (z + dz) * size + (x + dx);
                if (i >= 0 && i < polder.heights.length) {
                    polder.heights[i] = Math.max(polder.heights[i], seaLevel + 2);
                    polder.surface[i] = P.stone;
                }
            }
        }
    }

    const quayAt = polder.toWorld(
        mid + Math.sin(facing) * (quayEnd - 1),
        mid + Math.cos(facing) * (quayEnd - 1),
    );
    const quayFoot = polder.toWorld(
        mid + Math.sin(facing) * (quayCoast - 1),
        mid + Math.cos(facing) * (quayCoast - 1),
    );
    const quay = {
        // The stone itself, as a line: what a boat lies against.
        line: { ax: quayFoot.x, az: quayFoot.z, bx: quayAt.x, bz: quayAt.z },
        // Where the passengers stand, and where a boat lies to take them.
        landing: new THREE.Vector3(quayAt.x, (seaLevel + 2) * voxelSize, quayAt.z),
        mooring: new THREE.Vector3(
            quayAt.x + Math.sin(facing) * 4.5,
            seaLevel * voxelSize,
            quayAt.z + Math.cos(facing) * 4.5,
        ),
        facing,
    };

    env.addLight(quay.landing.clone().setY(quay.landing.y + 2), {
        color: 0xffc27a, intensity: 1.1, range: 12 * voxelSize * 2.4,
    });

    // --- the village: green boarded houses, huddled ------------------------
    let placed = 0;
    for (let i = 0; i < 30 && placed < 9; i++) {
        const a = bearing + Math.PI + (rng() - 0.5) * 2.2;
        const r = size * (0.1 + rng() * 0.17);
        const hx = Math.round(mid + Math.sin(a) * r);
        const hz = Math.round(mid + Math.cos(a) * r);
        if (!polder.isDryLand(hx, hz)) continue;
        const ok = stampHouse(grid, props, polder, hx, hz, {
            w: 5 + Math.floor(rng() * 2),
            d: 5,
            wallHeight: 4,
            wall: P.wall,
            roof: P.roof,
            rng,
        });
        if (ok) placed++;
    }

    // The real Paard van Marken is Oc W 8s: occulting white, lit most of the
    // time with one brief eclipse every eight seconds. It does not rotate at
    // all — but a light that only sits there is dull to sail past, so it keeps
    // a slow sweep and wears its real characteristic on top of it.
    const PERIOD = 8;        // seconds, the eclipse cycle
    const ECLIPSE = 1.1;     // seconds dark within it
    const TURN = 24;         // seconds for a full revolution

    let turn = rng() * Math.PI * 2;
    let phase = rng() * PERIOD;
    return {
        group,
        pick,
        quay,
        position: new THREE.Vector3(at.x, (seaLevel + 3) * voxelSize, at.z),
        /** @param night 0..1 from the environment */
        update(dt, night) {
            turn += dt * ((Math.PI * 2) / TURN);
            pivot.rotation.y = turn;

            // The shader needs the lamp in world space to measure reach from.
            beam.getWorldPosition(beam.material.uniforms.uLamp.value);

            // The eclipse: down and back up rather than snapped off, because
            // an occulting light dips through its screen rather than blinking.
            phase = (phase + dt) % PERIOD;
            const into = Math.min(phase, PERIOD - phase) / (ECLIPSE / 2);
            const lit = 0.12 + 0.88 * Math.min(1, into);

            // Invisible by day: a lighthouse in sunshine is just a tower.
            const dark = Math.max(0, night - 0.25) / 0.75;
            beam.material.uniforms.uStrength.value = 0.5 * dark * lit;
        },
    };
}
